import { useMutation, useQuery, useQueryClient } from 'react-query';
import feathersClient from '../../../apiClient';
import { getPaginatedResponse } from '../../../apiClient/utils';
import { TyTable, TyTableCell, TyTableHead, TyTableRow } from '@tymbe/ty-components/table';
import { TableHeadCellSortable } from '../../../components/Table/TableHeadCellSortable';
import { TableHead, TablePagination, useTablePagination } from '../../../components/Table';
import useTableSort from '../../../components/Table/useTableSort';
import Spinner from '../../../components/Spinner';
import { TyTableRowExpandable } from './components/TyTableRowExpandable';
import PersonName from '../../../components/dataEntity/PersonName';
import CompanyName from '../../../components/dataEntity/CompanyName';
import moment from 'moment';
import { JmhzRegzecData } from '@tymbe/schema/jmhz-regzec.interface';
import { CzechContractType } from '@tymbe/legislatives/czechia/CzechContractType';
import { JmhzRegzecState } from '@tymbe/schema/enums';
import downloadBlobResponse from '../../../utils/downloadBlobResponse';
import { PrimaryButton } from '@tymbe/ty-components/button';
import { toast } from 'react-toastify';
import { FeathersError, NotFound } from '@feathersjs/errors';

interface RegzecExportPageProps {
  query?: object;
}

const RegzecExportPage = () => {
  const { addSort, sortString: sort } = useTableSort({
    initialSortField: 'jmhz_regzec.created_at',
    initialSort: -1,
  });
  const { setPageParams, pageSize, pageStart } = useTablePagination();
  const [field, sortOrder] = sort?.split('=') ?? ['jmhz_regzec.created_at', -1];

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: [
      'jmhz',
      'regzec-export',
      {
        'jmhz_regzec.state': JmhzRegzecState.READY,
        $sort: Number(sortOrder) ? { [field]: Number(sortOrder) } : undefined,
        $limit: pageSize,
        $skip: pageStart,
      },
    ] as const,
    queryFn: async ({ queryKey: [, , q] }) => {
      const result = await feathersClient.service('jmhz-regzec').find({
        query: {
          $joinRelation: '[ppv.employee]',
          // $eager: '[contracts.documentType]',
          $eager: '[ppv.employee]',
          // 'documentType.type': { $in: Object.values(ContractDocumentTypes) },
          ...q,
          $sort: {
            ...q.$sort,
          },
        },
      });

      return getPaginatedResponse(result);
    },
  });

  const download = useMutation({
    mutationKey: 'regzec-export',
    mutationFn: async () => {
      return feathersClient.service('jmhz-regzec-download').find();
    },
    onSuccess: (data) => {
      const blob = new Blob([Uint8Array.from(data, (c: string) => c.charCodeAt(0))], { type: 'application/zip' });
      downloadBlobResponse(blob, `jmhz-regzec-${moment().format('YYYY-MM-DD-HH-mm')}.zip`);
      queryClient.invalidateQueries('jmhz');
    },
    onError: (error) => {
      if (error instanceof NotFound) {
        toast.error('Žádná data k exportu');
        return;
      }

      if (error instanceof FeathersError) {
        toast.error(`Chyba při stahování dat: ${error.message}`);
        return;
      }

      toast.error(`Chyba při stahování dat: ${error}`);
    },
  })

  return (
    <>
      <PrimaryButton loading={download.isLoading} onClick={() => download.mutate()} className="self-end">Exportovat</PrimaryButton>
      <TyTable className="w-full">
        <TableHead>
          <tr>
            <TyTableCell colSpan={10} className="!p-0 w-full border-t-transparent border-x-transparent"></TyTableCell>
          </tr>
          <TyTableRow>
            <TyTableCell className="w-7 border-r-transparent" />
            <TableHeadCellSortable {...addSort('id')}>ID</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('ppv.employer_id')}>Firma</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('ppv.employee_id')}>Tymber ID</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('ppv:employee.last_name')}>Tymber</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('ppv.contract_type')}>Typ smlouvy</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('ppv.group')}>Skupina</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('ppv.is_small')}>Malá</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('state')}>Stav</TableHeadCellSortable>
            <TableHeadCellSortable {...addSort('jmhz_regzec.created_at')}>Poslední data</TableHeadCellSortable>
          </TyTableRow>
        </TableHead>
        <tbody>
          {isLoading && (
            <TyTableRow>
              <TyTableCell colSpan={10} align="center">
                <Spinner show={isLoading} className="w-6" />
              </TyTableCell>
            </TyTableRow>
          )}
          {data?.data.length === 0 ? (
            <TyTableRow>
              <TyTableCell colSpan={10} align="center">
                Žádná data
              </TyTableCell>
            </TyTableRow>
          ) : null}

          {data?.data.map((item) => (
            <TyTableRowExpandable
              className="bg-bg h-10 [&>td:first-child]:border-r-transparent"
              key={item.id}
              cols={[
                (expanded, cantExpand) => (cantExpand ? '!' : expanded ? '˅' : '˃'),
                item.id,
                item.ppv ? <CompanyName company_id={item.ppv.employer_id} link /> : '-',
                item.ppv?.employee_id,
                item.ppv ? <PersonName person_id={item.ppv.employee_id} link /> : '-',
                item.ppv?.contract_type,
                item.ppv?.group,
                item.ppv?.is_small ? 'Ano' : 'Ne',
                item.state,
                item.created_at,
              ]}
            >
              <TyTableRow>
                <TyTableCell colSpan={10} className="!p-0">
                  <TyTable className="[&_td]:p-1 [&_th]:p-1 w-full table-fixed">
                    <thead>
                      <TyTableRow className="*:p-1">
                        <TyTableHead className="w-1/2">Podání</TyTableHead>
                        <TyTableHead className="w-1/2">Odpověď</TyTableHead>
                      </TyTableRow>
                    </thead>
                    <tbody>
                      <TyTableRow className="*:p-1">
                        <TyTableCell className="w-1/2 overflow-auto">
                          <pre>{item.request_xml}</pre>
                        </TyTableCell>
                        <TyTableCell className="w-1/2 overflow-auto">
                          <pre>{item.response_xml}</pre>
                        </TyTableCell>
                      </TyTableRow>
                    </tbody>
                  </TyTable>
                </TyTableCell>
              </TyTableRow>
            </TyTableRowExpandable>
          ))}
        </tbody>
      </TyTable>
      <TablePagination {...setPageParams()} rowsCount={data?.total} />
    </>
  );
};

export default RegzecExportPage;
